if (!import.meta.env.VITE_BASE_URL) {
    console.warn('VITE_BASE_URL is not set, using current host');
}

const getBaseHostname = (): string => {
    if (typeof window !== 'undefined') {
        return window.location.hostname;
    }
    return 'localhost';
};

// Construct the full domain with appropriate port
export const baseDomain = (() => {
    const configuredUrl = import.meta.env.VITE_BASE_URL;
    if (configuredUrl) {
        return configuredUrl.trim();
    }

    return getBaseHostname();
})();

export const baseUrl = (import.meta.env.VITE_BASE_URL || '').trim();

const isSojiAIAddress =
    baseDomain.includes('localhost') || baseDomain.includes('.soji.ai');
export const enableLangfuse = isSojiAIAddress;
export const langfuseLink =
    'https://langfuse.soji.ai/project/clzr4rej000rz14777ml93xb4/traces?dateRange=30+min';

const KNOWN_ENVIRONMENTS: readonly string[] = [
    'acl',
    'ade',
    'ap',
    'demo',
    'dev',
    'preprod',
    'staging',
];

const LOCAL_HOSTNAMES: readonly string[] = ['localhost', '127.0.0.1', '[::1]'];

// Testbeds and review environments are served from app-<name>.test.soji.ai.
const TESTBED_HOSTNAME = /^app-[a-z0-9-]+\.test\.soji\.ai$/;

/**
 * One image is deployed to every environment, so env can't be baked in at build
 * time. An unrecognised host resolves to `unknown` rather than `development`,
 * which keeps it outside `isInternalEnvironment` so gates keyed off it fail
 * closed.
 */
export function resolveEnvironment(hostname: string): string {
    if (LOCAL_HOSTNAMES.includes(hostname)) {
        return 'development';
    }
    if (TESTBED_HOSTNAME.test(hostname)) {
        return 'testbed';
    }
    const candidate = hostname.match(/^app\.([^.]+)\.soji\.ai$/)?.[1];
    return candidate && KNOWN_ENVIRONMENTS.includes(candidate)
        ? candidate
        : 'unknown';
}

export const ENV =
    import.meta.env.VITE_ENV || resolveEnvironment(getBaseHostname());
export const VERSION = import.meta.env.VITE_GIT_SHA || '';

const INTERNAL_ENVIRONMENTS: readonly string[] = [
    'development',
    'localhost',
    'test',
    'testbed',
    'dev',
    'staging',
    'preprod',
];

/**
 * Soji's own environments, as opposed to customer tenants (ap, acl) and
 * prospect-facing ones (demo, ade). Not a mirror of the backend's is_dev: this
 * deliberately includes staging and preprod, which is_dev excludes.
 */
export function isInternalEnvironment(env: string): boolean {
    return INTERNAL_ENVIRONMENTS.includes(env.trim().toLowerCase());
}

// Datadog RUM configuration
export const DATADOG_APPLICATION_ID =
    import.meta.env.VITE_DD_APPLICATION_ID || '';
export const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DD_CLIENT_TOKEN || '';
